import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  handleBulkDeleteAnnouncementsService,
  handleCreateAnnouncementTaskCommentService,
  handleDeleteAnnouncementService,
  handleDeleteAnnouncementTaskCommentService,
  handleDeleteAnnouncementTaskService,
  handleReadAnnouncementService,
  handleSoftDeleteAnnouncementService,
  handleToggleAnnouncementResponderService,
  handleUpdateAnnouncementService,
  handleUpdateAnnouncementTaskCommentService,
  handleUpdateAnnouncementTaskService,
} from "../services/operations/announcements";
import { useSnackbar } from "../context/SnackbarContextProvider";
import QUERY_KEYS from "../constants/queryKeys";
import { useOperationsContext } from "../context/OperationsContextProvider";

export const useAnnouncementTaskMutations = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const {
    briefSelectedDate,
    selectedAnnouncementId,
    clearAnnouncementSelection,
  } = useOperationsContext();

  // 🔹 Delete Task
  const deleteAnnouncementTaskMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_ANNOUNCEMENT_TASK],
    mutationFn: handleDeleteAnnouncementTaskService,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
          briefSelectedDate,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "משימת ההודעה נמחקה בהצלחה",
        type: "success",
      });
    },
  });

  // 🔹 Delete Task Comment
  const deleteAnnouncementTaskCommentMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_ANNOUNCEMENT_TASK_COMMENT],
    mutationFn: handleDeleteAnnouncementTaskCommentService,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "ההערה נמחקה בהצלחה ",
        type: "success",
      });
    },
  });

  // 🔹 Update Task
  const updateAnnouncementTaskMutation = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_ANNOUNCEMENT_TASK],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => handleUpdateAnnouncementTaskService(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
          briefSelectedDate,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "המשימה עודכנה בהצלחה",
        type: "success",
      });
    },
  });

  // 🔹 Create Task Comment
  const createAnnouncementTaskCommentMutation = useMutation({
    mutationKey: [QUERY_KEYS.CREATE_ANNOUNCEMENT_TASK_COMMENT],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => handleCreateAnnouncementTaskCommentService(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "נוספה הערה למשימה",
        type: "success",
      });
    },
  });

  // 🔹 Update Task Comment
  const updateAnnouncementTaskCommentMutation = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_ANNOUNCEMENT_TASK_COMMENT],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => handleUpdateAnnouncementTaskCommentService(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "נוספה הערה למשימה",
        type: "success",
      });
    },
  });

  // Delete announcement mutation
  const deleteAnnouncementMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_ANNOUNCEMENT],
    mutationFn: handleDeleteAnnouncementService,
    onSuccess: (data) => {
      // Invalidate and refetch announcements data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
          // briefSelectedDate,
        ],
      });

      showSnackbar({
        message: data?.message ?? "הודעה נמחקה בהצלחה",
        type: "success",
      });
    },
  });

  const bulkDeleteAnnouncementsMutation = useMutation({
    mutationKey: [QUERY_KEYS.BULK_DELETE_ANNOUNCEMENTS],
    mutationFn: handleBulkDeleteAnnouncementsService,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
      });

      clearAnnouncementSelection();

      showSnackbar({
        message: data?.message ?? "ההודעות נמחקו בהצלחה",
        type: "success",
      });
    },
  });

  // Soft-delete announcement (hides from operations/daily brief, keeps on calendar)
  const softDeleteAnnouncementMutation = useMutation({
    mutationKey: [QUERY_KEYS.SOFT_DELETE_ANNOUNCEMENT],
    mutationFn: ({ id, screen }: { id: string; screen: string }) =>
      handleSoftDeleteAnnouncementService(id, screen),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
          briefSelectedDate,
        ],
      });

      showSnackbar({
        message: data?.message ?? "הודעה נמחקה בהצלחה",
        type: "success",
      });
    },
  });

  // toggle announcement responder (thumbs up)
  const toggleAnnouncementResponderMutation = useMutation({
    mutationKey: [QUERY_KEYS.TOGGLE_ANNOUNCEMENT_RESPONDER],
    mutationFn: (id: string) => handleToggleAnnouncementResponderService(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE,
          // briefSelectedDate,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      if (data?.data?.isRead === false)
        showSnackbar({ message: "השיוך שלך הוסר", type: "info" });
      else
        showSnackbar({
          message: data?.message ?? "משימת ההודעה נמחקה בהצלחה",
          type: "success",
        });
    },
  });

  // update announcement
  const updateAnnouncementMutation = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_ANNOUNCEMENT],
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, any>;
    }) => handleUpdateAnnouncementService(id, payload),
    onSuccess: (data) => {
      // Invalidate and refetch announcements data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
      });

      if (selectedAnnouncementId) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT,
            selectedAnnouncementId,
          ],
        });
      }

      showSnackbar({
        message: data?.message ?? "הודעה עודכנה בהצלחה",
        type: "success",
      });
    },
  });

  const readAnnouncementMutation = useMutation({
    mutationFn: (obj: { id: string; payload: { userId?: string } }) =>
      handleReadAnnouncementService(obj?.id, obj?.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
    },
  });

  return {
    deleteAnnouncementTaskMutation,
    deleteAnnouncementTaskCommentMutation,
    updateAnnouncementTaskMutation,
    createAnnouncementTaskCommentMutation,
    updateAnnouncementTaskCommentMutation,
    deleteAnnouncementMutation,
    bulkDeleteAnnouncementsMutation,
    softDeleteAnnouncementMutation,
    updateAnnouncementMutation,
    toggleAnnouncementResponderMutation,
    readAnnouncementMutation,
  };
};
